import React, { CSSProperties, Ref } from 'react';
import classNames from 'classnames';

import { ClosedCaptionStyleProps } from 'store-types';
import { Cue } from 'app-types';

import styles from './CueStyleBox.module.scss';

interface CueStyleBoxProps {
  className?: string | null;
  cue: Cue;
  cueBoxRef?: Ref<HTMLDivElement> | null;
  settings: ClosedCaptionStyleProps;
  style?: CSSProperties | null;
}

const CueStyleBox = ({
  cue,
  cueBoxRef = null,
  settings,
  style = null,
}: CueStyleBoxProps) => {
  const {
    textSize,
    textColor,
    textType,
    textOpacity,
    textShadow,
    textShadowColor,
    backgroundColor,
    backgroundOpacity,
    windowColor,
    windowOpacity,
  } = settings;

  const { align, vertical, positionAlign, position, size } = cue;

  let textPos;
  switch (positionAlign) {
    case 'middle':
      textPos = (position as number) - (size as number) / 2;
      break;
    case 'end':
      textPos = (position as number) - (size as number);
      break;
    case 'start':
    default:
      textPos = position;
  }

  const windowStyle: CSSProperties = {};
  if (vertical === '') {
    windowStyle.left = `${textPos}%`;
    windowStyle.width = `${size}%`;
  } else {
    windowStyle.top = `${textPos}%`;
    windowStyle.height = `${size}%`;
  }

  return (
    <div
      className={classNames(
        styles.window,
        styles[`cue-align-${align}`],
        styles[`window-color-${windowColor}`],
        styles[`window-opacity-${windowOpacity}`],
        styles[`cue-veritcal-${vertical === '' ? 'horz' : vertical}`],
      )}
      style={{ ...windowStyle, ...style }}
      ref={cueBoxRef}
    >
      <div
        className={classNames(
          'd-inline-block',
          styles[`background-color-${backgroundColor}`],
          styles[`background-opacity-${backgroundOpacity}`],
        )}
      >
        <div
          className={classNames(
            styles.text,
            styles[textSize],
            styles[textType],
            styles[`text-color-${textColor}`],
            styles[`text-opacity-${textOpacity}`],
            styles[`text-shadow-${textShadow}`],
            styles[`text-shadow-color-${textShadowColor}`],
            styles[`writing-mode-${vertical === '' ? 'horz' : vertical}`],
          )}
          // After stripping out xml tags from the caption text
          // it may still contain html escaped characters like &gt;
          // setting inner html will allow for these characters to
          // display properly.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: cue.text as string }}
        />
      </div>
    </div>
  );
};

export default React.forwardRef<HTMLDivElement, Omit<CueStyleBoxProps, 'cueBoxRef'>>((props, ref) => {
  return <CueStyleBox {...props} cueBoxRef={ref} />;
});
